import { CheckIcon, TimeIcon } from '@chakra-ui/icons'
import { Badge, Box, Flex, IconButton, Input, Text, Tooltip, VStack } from '@chakra-ui/react'
import { DeleteButton, RoundedButton } from '@components/Buttons'
import { AutoResizeTextArea } from '@components/forms/Input/Text/TextArea/AutoResizeTextArea'
import { useProfile } from '@contexts/user/ProfileContext'
import { Encounter, EncounterType, PrenatalExamData, Problem, ProblemStatus } from '@hb/shared'
import { useCallback, useEffect, useRef } from 'react'
import { useField, useForm } from 'react-final-form'
import { SectionLabel } from '../../SectionLabel'

const generateId = () => `problem-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

const statusColors: Record<ProblemStatus, string> = {
  active: 'red',
  resolved: 'green',
  deferred: 'gray',
}

const statusLabels: Record<ProblemStatus, string> = {
  active: 'Active',
  resolved: 'Resolved',
  deferred: 'Deferred',
}

const ProblemRow = ({
  problem,
  onUpdate,
  onRemove,
}: {
  problem: Problem
  onUpdate: (updated: Problem) => void
  onRemove: (id: string) => void
}) => {
  const isResolved = problem.status === 'resolved'
  const isDeferred = problem.status === 'deferred'

  return (
    <Box
      border="1px solid"
      borderColor={isResolved ? 'green.200' : isDeferred ? 'gray.200' : 'red.200'}
      _dark={{
        borderColor: isResolved ? 'green.700' : isDeferred ? 'gray.600' : 'red.700',
      }}
      borderRadius="md"
      px={3}
      py={2}
      opacity={isResolved ? 0.7 : 1}
      w="100%">
      <Flex align="flex-start" gap={2}>
        <Box flex={1}>
          <AutoResizeTextArea
            value={problem.description}
            onChange={e => onUpdate({ ...problem, description: e.target.value })}
            placeholder="Describe the problem"
            size="sm"
            rows={1}
            resize="vertical"
            borderRadius="md"
            isReadOnly={isResolved}
            _readOnly={{ bg: 'gray.50', _dark: { bg: 'gray.800' } }}
          />
          {problem.notes !== undefined && (
            <Input
              mt={1}
              value={problem.notes ?? ''}
              onChange={e => onUpdate({ ...problem, notes: e.target.value })}
              placeholder="Notes (optional)"
              size="xs"
              borderRadius="md"
            />
          )}
        </Box>
        <Flex direction="column" align="center" gap={1} flexShrink={0}>
          <Badge colorScheme={statusColors[problem.status]} fontSize="2xs" px={2} py={0.5}>
            {statusLabels[problem.status]}
          </Badge>
          {!isResolved && (
            <Flex gap={1}>
              <Tooltip label="Mark resolved" placement="top" openDelay={400}>
                <IconButton
                  aria-label="Resolve"
                  icon={<CheckIcon />}
                  size="xs"
                  colorScheme="green"
                  variant="outline"
                  onClick={() => onUpdate({ ...problem, status: 'resolved' })}
                />
              </Tooltip>
              <Tooltip
                label={isDeferred ? 'Reactivate' : 'Defer to next visit'}
                placement="top"
                openDelay={400}>
                <IconButton
                  aria-label={isDeferred ? 'Reactivate' : 'Defer'}
                  icon={<TimeIcon />}
                  size="xs"
                  colorScheme={isDeferred ? 'blue' : 'gray'}
                  variant="outline"
                  onClick={() =>
                    onUpdate({ ...problem, status: isDeferred ? 'active' : 'deferred' })
                  }
                />
              </Tooltip>
              <DeleteButton
                variant="outline"
                noConfirm
                onDelete={() => onRemove(problem.id)}
                size="xs"
                itemName="Problem"
              />
            </Flex>
          )}
          {isResolved && (
            <RoundedButton
              size="xs"
              variant="ghost"
              colorScheme="gray"
              fontSize="2xs"
              onClick={() => onUpdate({ ...problem, status: 'active' })}>
              Reopen
            </RoundedButton>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}

export const ProblemListInput = () => {
  const form = useForm()
  const { encounters, assessmentId } = useProfile()
  const carriedForward = useRef(false)

  const {
    input: { value: problemList },
  } = useField<Problem[] | undefined>('data.problemList', { subscription: { value: true } })

  // Carry forward non-resolved problems from the most recent authenticated antepartum encounter
  useEffect(() => {
    if (carriedForward.current) return
    carriedForward.current = true
    const currentList = form.getState().values?.data?.problemList as Problem[] | undefined
    if (currentList?.length) return

    const priorEncounters = (encounters.dataArr as Encounter[])
      .filter(
        enc =>
          enc.assessmentId === assessmentId &&
          enc.type === EncounterType.ANTEPARTUM &&
          !!enc.authentication,
      )
      .sort((a, b) => {
        const aDate = (a.authentication as any)?.date ?? 0
        const bDate = (b.authentication as any)?.date ?? 0
        return bDate - aDate
      })

    const prior = priorEncounters[0]?.data as PrenatalExamData | undefined
    const priorProblems = prior?.problemList
    if (!priorProblems?.length) return

    const carried = priorProblems
      .filter(p => p.status !== 'resolved')
      .map(p => ({ ...p, status: 'active' as ProblemStatus }))
    if (carried.length) {
      form.change('data.problemList', carried)
    }
  }, [encounters.dataArr, assessmentId, form])

  const handleUpdate = useCallback(
    (updated: Problem) => {
      const current = (form.getState().values?.data?.problemList as Problem[]) ?? []
      form.change(
        'data.problemList',
        current.map(p => (p.id === updated.id ? updated : p)),
      )
    },
    [form],
  )

  const handleRemove = useCallback(
    (id: string) => {
      const current = (form.getState().values?.data?.problemList as Problem[]) ?? []
      form.change(
        'data.problemList',
        current.filter(p => p.id !== id),
      )
    },
    [form],
  )

  const handleAdd = useCallback(() => {
    const current = (form.getState().values?.data?.problemList as Problem[]) ?? []
    form.change('data.problemList', [
      ...current,
      { id: generateId(), description: '', status: 'active' as ProblemStatus },
    ])
  }, [form])

  const problems = Array.isArray(problemList) ? problemList : []
  const activeCount = problems.filter(p => p.status === 'active').length

  return (
    <Box w="100%">
      <Flex align="center" gap={3} mb={problems.length ? 2 : 0}>
        <SectionLabel mb={1}>Problem List</SectionLabel>
        {activeCount > 0 && (
          <Badge colorScheme="red" borderRadius="full" fontSize="2xs" px={2}>
            {activeCount} active
          </Badge>
        )}
      </Flex>
      {problems.length > 0 && (
        <VStack spacing={2} align="stretch" mb={2}>
          {problems.map(problem => (
            <ProblemRow
              key={problem.id}
              problem={problem}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
            />
          ))}
        </VStack>
      )}
      <RoundedButton size="xs" onClick={handleAdd}>
        + Add Problem
      </RoundedButton>
      {!problems.length && (
        <Text fontSize="xs" color="gray.400" ml={2} display="inline">
          — no active problems
        </Text>
      )}
    </Box>
  )
}
